import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authApiService } from '../services/authApiService';
import { TeamReference } from '../types/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faSpinner, faChevronDown } from '@/icons';
import { CustomTooltip } from './CustomTooltip';

function useTeams(isLawClerk: boolean) {
    const [teams, setTeams] = useState<TeamReference[]>([]);
    const load = useCallback(async () => {
        if (!isLawClerk) return;
        try {
            const response = await authApiService.getMyTeams();
            setTeams(response.data.teams || []);
        } catch {
            setTeams([]);
        }
    }, [isLawClerk]);
    useEffect(() => { void load(); }, [load]);
    return teams;
}

/**
 * Header bar team switcher for Law Clerk users — shows current team name as a dropdown.
 * Reloads the page after switching so all context is reset.
 */
export const HeaderTeamSwitcher: React.FC = () => {
    const { user, switchTeam } = useAuthContext();
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const isLawClerk = (user?.businessRole || '').toLowerCase() === 'law clerk';
    const teams = useTeams(isLawClerk);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    if (!isLawClerk) return null;

    const handleSwitch = async (teamId: string) => {
        if (teamId === user?.teamId || switching) return;
        setSwitching(true);
        setOpen(false);
        try {
            await switchTeam(teamId);
            window.location.reload();
        } catch {
            setSwitching(false);
        }
    };

    const currentTeamName = user?.team?.name ?? 'Team';

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: theme.spacing.xs,
                    marginLeft: theme.spacing.md,
                    background: theme.colors.surfaceSecondary,
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: theme.borderRadius.md,
                    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                    cursor: 'pointer',
                    color: theme.colors.text.primary,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    maxWidth: '220px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {switching
                    ? <FontAwesomeIcon icon={faSpinner} spin style={{ flexShrink: 0 }} />
                    : null
                }
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentTeamName}</span>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', flexShrink: 0, opacity: 0.6 }} />
            </button>

            {open && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    zIndex: 2000,
                    background: theme.colors.surface,
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.md,
                    minWidth: '200px',
                    overflow: 'hidden',
                }}>
                    {teams.map(t => (
                        <button
                            key={t.teamId}
                            onClick={() => void handleSwitch(t.teamId)}
                            style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                background: t.teamId === user?.teamId ? theme.colors.surfaceSecondary : 'none',
                                border: 'none',
                                cursor: t.teamId === user?.teamId ? 'default' : 'pointer',
                                color: theme.colors.text.primary,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: t.teamId === user?.teamId
                                    ? theme.typography.fontWeight.bold
                                    : theme.typography.fontWeight.normal,
                            }}
                        >
                            {t.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Profile menu team switcher for Law Clerk users (existing behavior).
 */
export const TeamSwitcher: React.FC = () => {
    const { user, switchTeam } = useAuthContext();
    const theme = useTheme();

    const [open, setOpen] = useState(false);
    const [switching, setSwitching] = useState(false);

    const isLawClerk = (user?.businessRole || '').toLowerCase() === 'law clerk';
    const teams = useTeams(isLawClerk);

    if (!isLawClerk) return null;

    const handleSwitch = async (teamId: string) => {
        if (teamId === user?.teamId || switching) return;
        setSwitching(true);
        setOpen(false);
        try {
            await switchTeam(teamId);
            window.location.reload();
        } catch {
            setSwitching(false);
        }
    };

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <CustomTooltip text="Switch team">
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    background: 'none',
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: theme.borderRadius.sm,
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    cursor: 'pointer',
                    color: theme.colors.text.secondary,
                    fontSize: theme.typography.fontSize.xs,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: theme.spacing.xs,
                    marginTop: theme.spacing.xs,
                    transition: `all ${theme.transitions.normal}`,
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary;
                    e.currentTarget.style.borderColor = theme.colors.primary;
                    e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = theme.colors.border.default;
                    e.currentTarget.style.color = theme.colors.text.secondary;
                }}
            >
                {switching
                    ? <FontAwesomeIcon icon={faSpinner} spin />
                    : <FontAwesomeIcon icon={faExchangeAlt} />
                }
                Switch team
            </button>
            </CustomTooltip>

            {open && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: theme.colors.background,
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.md,
                    minWidth: '200px',
                    marginTop: theme.spacing.xs,
                }}>
                    {teams.map(t => (
                        <button
                            key={t.teamId}
                            onClick={() => void handleSwitch(t.teamId)}
                            style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                background: t.teamId === user?.teamId
                                    ? theme.colors.surfaceSecondary
                                    : 'none',
                                border: 'none',
                                cursor: t.teamId === user?.teamId ? 'default' : 'pointer',
                                color: theme.colors.text.primary,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: t.teamId === user?.teamId
                                    ? theme.typography.fontWeight.bold
                                    : theme.typography.fontWeight.normal,
                            }}
                        >
                            {t.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
